import { useEffect, useState } from "react";
import api from "@/lib/api";
import { toast } from "sonner";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import { Switch } from "@/components/ui/switch";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger, DialogFooter } from "@/components/ui/dialog";
import { Megaphone, Plus, Pin, Trash2 } from "lucide-react";
import { useAuth } from "@/context/AuthContext";

export default function Announcements() {
    const { user } = useAuth();
    const isStaff = user?.role === "ADMIN" || user?.role === "FACULTY";
    const [items, setItems] = useState([]);
    const [batches, setBatches] = useState([]);
    const [courses, setCourses] = useState([]);
    const [open, setOpen] = useState(false);
    const [form, setForm] = useState({ title: "", body: "", scope: "GLOBAL", category: "Notice", pinned: false, batch_id: "", course_id: "" });

    const load = async () => {
        const [a, b, c] = await Promise.all([api.get("/announcements"), api.get("/batches"), api.get("/courses")]);
        setItems(a.data.items);
        setBatches(b.data.items);
        setCourses(c.data.items);
    };
    useEffect(() => { load(); }, []);

    const submit = async (e) => {
        e.preventDefault();
        try {
            await api.post("/announcements", form);
            toast.success("Announcement posted");
            setOpen(false);
            setForm({ title: "", body: "", scope: "GLOBAL", category: "Notice", pinned: false, batch_id: "", course_id: "" });
            load();
        } catch (err) { toast.error(err.response?.data?.detail || "Failed"); }
    };

    const del = async (id) => {
        if (!window.confirm("Delete this announcement?")) return;
        await api.delete(`/announcements/${id}`);
        toast.success("Deleted");
        load();
    };

    return (
        <div className="space-y-6" data-testid="announcements-page">
            <div className="flex items-end justify-between flex-wrap gap-3">
                <div>
                    <div className="text-xs uppercase tracking-widest font-bold text-muted-foreground">Updates</div>
                    <h1 className="font-display text-3xl lg:text-4xl mt-1">Announcements</h1>
                </div>
                {isStaff && (
                    <Dialog open={open} onOpenChange={setOpen}>
                        <DialogTrigger asChild><Button className="mx-btn-primary" data-testid="add-announcement-btn"><Plus className="h-4 w-4 mr-2" /> New announcement</Button></DialogTrigger>
                        <DialogContent className="max-w-lg">
                            <DialogHeader><DialogTitle>Post announcement</DialogTitle></DialogHeader>
                            <form onSubmit={submit} className="space-y-3">
                                <div><Label>Title</Label><Input required value={form.title} onChange={e => setForm({ ...form, title: e.target.value })} data-testid="ann-title" /></div>
                                <div><Label>Body</Label><Textarea required value={form.body} onChange={e => setForm({ ...form, body: e.target.value })} rows={4} data-testid="ann-body" /></div>
                                <div className="grid grid-cols-2 gap-3">
                                    <div>
                                        <Label>Scope</Label>
                                        <Select value={form.scope} onValueChange={(v) => setForm({ ...form, scope: v })}>
                                            <SelectTrigger><SelectValue /></SelectTrigger>
                                            <SelectContent>
                                                <SelectItem value="GLOBAL">All students</SelectItem>
                                                <SelectItem value="BATCH">A batch</SelectItem>
                                                <SelectItem value="COURSE">A course</SelectItem>
                                            </SelectContent>
                                        </Select>
                                    </div>
                                    <div>
                                        <Label>Category</Label>
                                        <Select value={form.category} onValueChange={(v) => setForm({ ...form, category: v })}>
                                            <SelectTrigger><SelectValue /></SelectTrigger>
                                            <SelectContent>
                                                <SelectItem value="Notice">Notice</SelectItem>
                                                <SelectItem value="Class Update">Class Update</SelectItem>
                                                <SelectItem value="New Assignment">New Assignment</SelectItem>
                                                <SelectItem value="Important Notice">Important</SelectItem>
                                            </SelectContent>
                                        </Select>
                                    </div>
                                </div>
                                {form.scope === "BATCH" && (
                                    <div><Label>Batch</Label>
                                        <Select value={form.batch_id} onValueChange={(v) => setForm({ ...form, batch_id: v })}>
                                            <SelectTrigger><SelectValue placeholder="Select batch" /></SelectTrigger>
                                            <SelectContent>{batches.map(b => <SelectItem key={b.id} value={b.id}>{b.name}</SelectItem>)}</SelectContent>
                                        </Select>
                                    </div>
                                )}
                                {form.scope === "COURSE" && (
                                    <div><Label>Course</Label>
                                        <Select value={form.course_id} onValueChange={(v) => setForm({ ...form, course_id: v })}>
                                            <SelectTrigger><SelectValue placeholder="Select course" /></SelectTrigger>
                                            <SelectContent>{courses.map(c => <SelectItem key={c.id} value={c.id}>{c.title}</SelectItem>)}</SelectContent>
                                        </Select>
                                    </div>
                                )}
                                <div className="flex items-center gap-3"><Switch checked={form.pinned} onCheckedChange={(v) => setForm({ ...form, pinned: v })} /><Label className="!mt-0">Pin to top</Label></div>
                                <DialogFooter><Button type="submit" className="mx-btn-primary" data-testid="ann-submit">Post</Button></DialogFooter>
                            </form>
                        </DialogContent>
                    </Dialog>
                )}
            </div>

            <div className="space-y-4">
                {items.map(a => (
                    <Card key={a.id} className="mx-card" data-testid={`ann-${a.id}`}>
                        <CardContent className="p-5">
                            <div className="flex items-start gap-3">
                                <div className="h-10 w-10 rounded-xl bg-amber-gradient flex items-center justify-center text-white shadow-mx-md"><Megaphone className="h-4 w-4" /></div>
                                <div className="flex-1">
                                    <div className="flex items-center gap-2 flex-wrap">
                                        {a.pinned && <Pin className="h-3.5 w-3.5 text-[#FFA81B]" />}
                                        <Badge variant="outline" className="text-[10px]">{a.category}</Badge>
                                        <Badge variant="secondary" className="text-[10px]">{a.scope}</Badge>
                                        <span className="text-xs text-muted-foreground">by {a.created_by_name} · {new Date(a.created_at).toLocaleString()}</span>
                                    </div>
                                    <div className="font-bold text-base mt-2">{a.title}</div>
                                    <div className="text-sm text-muted-foreground mt-1 whitespace-pre-wrap">{a.body}</div>
                                </div>
                                {isStaff && <Button variant="ghost" size="icon" onClick={() => del(a.id)} data-testid={`delete-ann-${a.id}`}><Trash2 className="h-4 w-4 text-[#E11D48]" /></Button>}
                            </div>
                        </CardContent>
                    </Card>
                ))}
                {items.length === 0 && <div className="mx-card p-12 text-center text-muted-foreground">No announcements yet.</div>}
            </div>
        </div>
    );
}
